import { create } from 'zustand';
import { DateRange, RangeType, getPresetRange, getToday } from '../utils/dateUtils';

/**
 * Date Range Selection Store
 *
 * Manages the selected date range for the Profit Monitor dashboard.
 * - State persists in memory during the user session (no localStorage/sessionStorage)
 * - Defaults to "Today"
 * - Supports both preset and custom date ranges
 * - Provides change tracking via rangeChangeCounter for reactive updates
 */

interface DateRangeState {
  // State
  rangeType: RangeType; // Current preset type or 'custom'
  dateRange: DateRange; // Current start and end dates
  rangeChangeCounter: number; // Incremented when range changes (for triggering reactive updates)

  // Actions
  setPreset: (preset: RangeType) => void;
  setCustomRange: (range: DateRange) => void;
  setDateRange: (rangeType: RangeType, range: DateRange) => void;
  resetToDefault: () => void;
}

export const useDateRangeStore = create<DateRangeState>((set) => ({
  // Initial state - default to Today
  rangeType: 'today',
  dateRange: getToday(),
  rangeChangeCounter: 0,

  // Actions
  setPreset: (preset: RangeType) => {
    if (preset === 'custom') {
      // Don't calculate for custom - user must use setCustomRange
      return;
    }
    const range = getPresetRange(preset);
    set((state) => ({
      rangeType: preset,
      dateRange: range,
      rangeChangeCounter: state.rangeChangeCounter + 1
    }));
  },

  setCustomRange: (range: DateRange) =>
    set((state) => ({
      rangeType: 'custom',
      dateRange: range,
      rangeChangeCounter: state.rangeChangeCounter + 1
    })),

  setDateRange: (rangeType: RangeType, range: DateRange) =>
    set((state) => ({
      rangeType,
      dateRange: range,
      rangeChangeCounter: state.rangeChangeCounter + 1
    })),

  resetToDefault: () => {
    const todayRange = getToday();
    set((state) => ({
      rangeType: 'today',
      dateRange: todayRange,
      rangeChangeCounter: state.rangeChangeCounter + 1
    }));
  }
}));

/**
 * Hook to get the current date range
 * Returns both the range type and the actual date range
 */
export const useDateRange = () => {
  const rangeType = useDateRangeStore((state) => state.rangeType);
  const dateRange = useDateRangeStore((state) => state.dateRange);
  const rangeChangeCounter = useDateRangeStore((state) => state.rangeChangeCounter);

  return { rangeType, dateRange, rangeChangeCounter };
};

/**
 * Hook to subscribe to date range changes
 * Returns the change counter that increments whenever the date range is modified
 * Components can use this to trigger effects when the range changes
 */
export const useDateRangeChangeCounter = (): number => {
  return useDateRangeStore((state) => state.rangeChangeCounter);
};
